import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Ic, ICONS } from '../data/icons';
import { Card, CardHeader, Badge, toast, SearchInput, Select, TableSkeleton, EmptyState } from '../components/shared';
import { request } from '../api/client';

const SOURCE_COLOR = { vpp: '#22c55e', mdm: '#3b82f6' };
const SOURCE_LABEL = { vpp: 'VPP', mdm: 'Store' };

export default function Apps({ C }) {
  const [apps, setApps] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [selectedApp, setSelectedApp] = useState(null);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [deploying, setDeploying] = useState(false);
  const [counts, setCounts] = useState({ mdm: 0, vpp: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [appsData, devicesData] = await Promise.all([
        request('/api/enrollment/all-apps'),
        request('/devices').then(d => d?.items ?? d),
      ]);
      const items = Array.isArray(appsData?.items) ? appsData.items : [];
      const devList = Array.isArray(devicesData) ? devicesData : (devicesData?.items || []);
      setApps(items);
      setDevices(devList);
      setCounts({ mdm: appsData?.mdm_count || 0, vpp: appsData?.vpp_count || 0 });
      if (items.length > 0) setSelectedApp(prev => prev || items[0]);
    } catch (err) {
      console.error(err);
      toast.error('Uygulamalar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => apps.filter(app => {
    const q = search.toLowerCase();
    const ms = !q || app.name.toLowerCase().includes(q) || (app.bundle_id && app.bundle_id.toLowerCase().includes(q));
    const mf = sourceFilter === 'all' || app.source === sourceFilter;
    return ms && mf;
  }), [apps, search, sourceFilter]);

  const toggleDevice = (id) => setSelectedDeviceIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleAllDevices = () => setSelectedDeviceIds(selectedDeviceIds.length === devices.length ? [] : devices.map(d => d.id));

  const handleDeploy = async () => {
    if (!selectedApp || selectedDeviceIds.length === 0) {
      toast.error('Uygulama ve en az bir cihaz seçin');
      return;
    }
    setDeploying(true);
    try {
      const result = await request('/api/enrollment/smart-distribute', {
        method: 'POST',
        body: JSON.stringify({ app_id: selectedApp.id, device_ids: selectedDeviceIds }),
      });
      const msg = result.source === 'vpp'
        ? selectedApp.name + ' → ' + result.distributed + ' cihaza VPP ile dağıtıldı (' + result.vppAssigned + ' lisans)'
        : selectedApp.name + ' → ' + result.distributed + ' cihaza dağıtım kuyruğa alındı';
      toast.success(msg);
      setSelectedDeviceIds([]);
      load();
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Dağıtım başarısız');
    } finally {
      setDeploying(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
        <SearchInput value={search} onChange={setSearch} placeholder="Uygulama adı veya Bundle ID ara..." C={C} style={{ flex: '1 1 200px', maxWidth: 320 }} />
        <Select C={C} value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} style={{ width: 'auto', height: 38 }}>
          <option value="all">Tümü ({counts.mdm + counts.vpp})</option>
          <option value="vpp">VPP Lisanslı ({counts.vpp})</option>
          <option value="mdm">Manuel ({counts.mdm})</option>
        </Select>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,2fr) minmax(0,1.3fr)', gap: 16 }}>
        <Card C={C}>
          <CardHeader C={C} title="Uygulama Kataloğu" action={<span style={{ fontSize: 11, color: C.textFaint }}>{filtered.length} / {apps.length}</span>} />
          {loading && <TableSkeleton rows={5} cols={5} C={C} />}
          {!loading && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
                <thead>
                  <tr style={{ background: C.surface2 }}>
                    {['Uygulama', 'Bundle ID', 'Sürüm', 'Kaynak', 'Lisans'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '9px 16px', fontSize: 10.5, fontWeight: 700, color: C.textFaint, letterSpacing: '0.7px', textTransform: 'uppercase', borderBottom: '1px solid ' + C.border }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(app => (
                    <tr key={app.id} style={{ transition: 'background 0.1s', cursor: 'pointer', background: selectedApp?.id === app.id ? C.accentBg : 'transparent' }}
                      onClick={() => setSelectedApp(app)}>
                      <td style={{ padding: '11px 16px', borderBottom: '1px solid ' + C.border }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {app.icon_url ? (
                            <img src={app.icon_url} alt="" style={{ width: 28, height: 28, borderRadius: 8, flexShrink: 0 }} />
                          ) : (
                            <div style={{ width: 28, height: 28, borderRadius: 8, background: C.accentBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <Ic d={ICONS.package} size={14} s={C.accent} />
                            </div>
                          )}
                          <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{app.name}</div>
                        </div>
                      </td>
                      <td style={{ padding: '11px 16px', borderBottom: '1px solid ' + C.border }}>
                        <span style={{ fontSize: 11, color: C.textSub, fontFamily: 'monospace' }}>{app.bundle_id}</span>
                      </td>
                      <td style={{ padding: '11px 16px', borderBottom: '1px solid ' + C.border }}>
                        <span style={{ fontSize: 12, color: C.textSub, fontFamily: 'monospace' }}>{app.version}</span>
                      </td>
                      <td style={{ padding: '11px 16px', borderBottom: '1px solid ' + C.border }}>
                        <Badge color={SOURCE_COLOR[app.source]}>{SOURCE_LABEL[app.source]}</Badge>
                      </td>
                      <td style={{ padding: '11px 16px', borderBottom: '1px solid ' + C.border }}>
                        {app.source === 'vpp' ? (
                          <span style={{ fontSize: 11, color: C.textSub }}>{app.available_licenses} / {app.total_licenses}</span>
                        ) : (
                          <span style={{ fontSize: 11, color: C.textFaint }}>App Store</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filtered.length === 0 && <EmptyState icon="package" title="Uygulama bulunamadı" C={C} />}
            </div>
          )}
        </Card>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {selectedApp && (
            <Card C={C}>
              <CardHeader C={C} title="Uygulama Detayı" />
              <div style={{ padding: '16px 20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                  {selectedApp.icon_url ? (
                    <img src={selectedApp.icon_url} alt="" style={{ width: 48, height: 48, borderRadius: 12 }} />
                  ) : (
                    <div style={{ width: 48, height: 48, borderRadius: 12, background: C.accentBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Ic d={ICONS.package} size={24} s={C.accent} />
                    </div>
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>{selectedApp.name}</div>
                    <div style={{ fontSize: 11, color: C.textFaint, fontFamily: 'monospace' }}>{selectedApp.bundle_id}</div>
                  </div>
                  <Badge color={SOURCE_COLOR[selectedApp.source]}>{SOURCE_LABEL[selectedApp.source]}</Badge>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {[
                    ['Sürüm', selectedApp.version || '—'],
                    ['Kaynak', selectedApp.source === 'vpp' ? 'Apple VPP' : 'App Store'],
                    ...(selectedApp.source === 'vpp'
                      ? [['Toplam', selectedApp.total_licenses], ['Boş', selectedApp.available_licenses]]
                      : [['Kategori', selectedApp.category || 'Diğer'], ['Zorunlu', selectedApp.is_mandatory ? 'Evet' : 'Hayır']]
                    ),
                  ].map(([l, v]) => (
                    <div key={l} style={{ background: C.surface2, borderRadius: 9, padding: '8px 12px' }}>
                      <div style={{ fontSize: 10.5, color: C.textFaint, marginBottom: 3 }}>{l}</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          )}

          <Card C={C}>
            <CardHeader C={C} title="Dağıtım Paneli" />
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <div style={{ fontSize: 11.5, color: C.textFaint, marginBottom: 6, display: 'flex', justifyContent: 'space-between' }}>
                  <span>Hedef Cihazlar ({selectedDeviceIds.length} / {devices.length})</span>
                  <button onClick={toggleAllDevices} style={{ background: 'transparent', border: 'none', color: C.accent, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
                    {selectedDeviceIds.length === devices.length ? 'Hiçbiri' : 'Tümünü Seç'}
                  </button>
                </div>
                <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid ' + C.border, borderRadius: 9, padding: 4 }}>
                  {devices.length === 0 && <div style={{ padding: 12, fontSize: 12, color: C.textFaint, textAlign: 'center' }}>Cihaz yok</div>}
                  {devices.map(d => (
                    <label key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', cursor: 'pointer', borderRadius: 6, fontSize: 12, color: C.text }}>
                      <input type="checkbox" checked={selectedDeviceIds.includes(d.id)} onChange={() => toggleDevice(d.id)} />
                      <span style={{ flex: 1 }}>{d.name || 'Cihaz #' + d.id}</span>
                      <span style={{ fontSize: 10, color: C.textFaint }}>{d.os || ''}</span>
                    </label>
                  ))}
                </div>
              </div>
              {selectedApp && selectedDeviceIds.length > 0 && (
                <div style={{ background: C.accentBg, border: '1px solid ' + C.accentBorder, borderRadius: 9, padding: '10px 12px', fontSize: 12, color: C.textSub, lineHeight: 1.5 }}>
                  <strong style={{ color: C.text }}>{selectedApp.name}</strong> {selectedApp.source === 'vpp' ? '(VPP - Apple ID gerekmez)' : '(Store)'} <strong style={{ color: C.text }}>{selectedDeviceIds.length} cihaza</strong> dağıtılacak.
                </div>
              )}
              <button onClick={handleDeploy} disabled={deploying || !selectedApp || selectedDeviceIds.length === 0}
                style={{ padding: '9px 14px', borderRadius: 10, border: 'none', background: C.accent, color: '#fff', fontSize: 13, fontWeight: 700, cursor: deploying || !selectedApp || selectedDeviceIds.length === 0 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, opacity: deploying || !selectedApp || selectedDeviceIds.length === 0 ? 0.5 : 1 }}>
                <Ic d={ICONS.zap} size={14} s="#fff" /> {deploying ? 'Dağıtılıyor…' : 'Dağıtımı Başlat'}
              </button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
